import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Stack, Button, Chip,
  CircularProgress, Alert, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Cake as CakeIcon,
  ContactPhone as ContactPhoneIcon,
  SupervisorAccount as GuardianIcon,
  LocalHospital as MedicalIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  School as SchoolIcon,
  Home as HomeIcon,
  Checkroom as MerchIcon,
  Group as GroupIcon,
  LocalActivity as LocalActivityIcon
} from '@mui/icons-material';

// Configuration
// const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
// const API_URL = `${API_BASE_URL}/api/registrations`;
const API_BASE_URL = ''; 
const API_URL="/api/registrations"


// Dark System theme - matches EventsDashboard
const COLORS = Object.freeze({
  bg: '#232226',
  sidebar: '#1F1E21',
  card: '#2B2A2E',
  cardHover: '#333236',
  accent: '#6C5DD3',
  accentHover: '#7c6fe0',
  border: 'rgba(255,255,255,0.08)',
  textPrimary: '#ffffff',
  textSecondary: '#8E8E93',
  success: '#34c759',
  error: '#ff453a',
  warning: '#ff9f0a'
});

// Official Event Metadata for displaying event names
const EVENT_METADATA = Object.freeze({
  DCIRosewell26: { title: 'DCI - Rosewell, Georgia', date: 'June 8 - 13, 2026' },
  DCILawrence26: { title: 'DCI - Lawrence, Kansas', date: 'June 22 - 27, 2026' },
  DCICarlisle26: { title: 'DCI - Carlisle, Pennsylvania', date: 'July 27 - August 1, 2026' },
  PWKingwood26: { title: 'Performance Workshop - Kingwood, Texas', date: 'June 5 - 7, 2026' }
});

// Calculate age from date of birth
const calculateAge = (dob) => {
  if (!dob) return '';
  try {
    const today = new Date();
    const birthDate = new Date(dob);
    if (isNaN(birthDate.getTime())) return '';
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age >= 0 && age < 150 ? age : '';
  } catch {
    return '';
  }
};

// Get pronouns safely
const getPronouns = (pronouns) => {
  if (!pronouns) return '';
  if (typeof pronouns === 'object' && pronouns?.label) return pronouns.label;
  return String(pronouns);
};

// Input constants
const MAX_INPUT_LENGTH = 255;
const MAX_PHONE_LENGTH = 20;
const MAX_EMAIL_LENGTH = 254;

// Input sanitization utilities
const sanitizeInput = (value, maxLength = MAX_INPUT_LENGTH) => {
  if (value === null || value === undefined) return '';
  return String(value).trim().slice(0, maxLength);
};

const sanitizeEmail = (email) => {
  const sanitized = sanitizeInput(email, MAX_EMAIL_LENGTH).toLowerCase();
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(sanitized) ? sanitized : email;
};

const sanitizePhone = (phone) => {
  return sanitizeInput(phone, MAX_PHONE_LENGTH).replace(/[^\d+\-() ]/g, '');
};

// Reusable text field styling
const textFieldStyle = {
  '& .MuiInputBase-root': {
    color: COLORS.textPrimary,
    bgcolor: COLORS.bg,
    borderRadius: '8px',
    fontSize: '0.875rem',
  '&.Mui-disabled': {
      bgcolor: COLORS.bg, 
      WebkitTextFillColor: COLORS.textPrimary, 
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: COLORS.border, 
      },
    }
  },
  '& .MuiInputBase-input.Mui-disabled': {
    color: COLORS.textPrimary, 
    WebkitTextFillColor: COLORS.textPrimary, 
  },
  '& .MuiInputLabel-root': { color: COLORS.textSecondary, fontSize: '0.875rem' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.border },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.accent },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.accent }
};

// RegistrantForm component for editing
const RegistrantForm = ({ registrant, setRegistrant, eventId }) => {
  const handleChange = useCallback((field, value, sanitizer = sanitizeInput) => {
    setRegistrant(prev => ({ ...prev, [field]: sanitizer(value) }));
  }, [setRegistrant]);

  // Merchandise config based on event
  // const merchConfig = {
  //   DCIRosewell26: { sizeKey: 'RosewellDCITShirtSize', qtyKey: 'RosewellDCITShirtQuantity', label: 'Rosewell DCI' },
  //   DCILawrence26: { sizeKey: 'LawrenceDCITShirtSize', qtyKey: 'LawrenceDCITShirtQuantity', label: 'Lawrence DCI' },
  //   DCICarlisle26: { sizeKey: 'CarlisleDCITShirtSize', qtyKey: 'CarlisleDCITShirtQuantity', label: 'Carlisle DCI' },
  //   PWKingwood26: { sizeKey: 'KingwoodPWTShirtSize', qtyKey: 'KingwoodPWTShirtQuantity', label: 'Kingwood PW' }
  // };

  const merchConfig = {
  DCIRosewell26: {
    label: 'Rosewell DCI',
    items: [
      { type: 'T-Shirt', sizeKey: 'RosewellDCITShirtSize', qtyKey: 'RosewellDCITShirtQuantity' },
      { type: 'Sweatshirt', sizeKey: 'RosewellDCISweatshirtSize', qtyKey: 'RosewellDCISweatShirtQuantity' }
    ]
  },
  DCILawrence26: {
    label: 'Lawrence DCI',
    items: [
      { type: 'T-Shirt', sizeKey: 'LawrenceDCITShirtSize', qtyKey: 'LawrenceDCITShirtQuantity' },
      { type: 'Sweatshirt', sizeKey: 'LawrenceDCISweatShirtSize', qtyKey: 'LawrenceDCISweatShirtQuantity' }
    ]
  },
  DCICarlisle26: {
    label: 'Carlisle DCI',
    items: [
      { type: 'T-Shirt', sizeKey: 'CarlisleDCITShirtSize', qtyKey: 'CarlisleDCITShirtQuantity' },
      { type: 'Sweatshirt', sizeKey: 'CarlisleDCISweatShirtSize', qtyKey: 'CarlisleDCISweatShirtQuantity' }
    ]
  },
  PWKingwood26: {
    label: 'Kingwood PW',
    items: [
      { type: 'T-Shirt', sizeKey: 'KingwoodPWTShirtSize', qtyKey: 'KingwoodPWTShirtQuantity' },
      { type: 'Sweatshirt', sizeKey: 'KingwoodPWSweatShirtSize', qtyKey: 'KingwoodPWSweatShirtQuantity' }
    ]
  }
};

  const currentMerch = merchConfig[eventId];

  return (
    <Stack spacing={3} sx={{ width: '100%', px: 1 }}>
      {/* Participant Information */}
      <Box sx={{ flexShrink: 0 }}>
        <Typography variant="subtitle2" sx={{ color: COLORS.accent, mb: 2, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
          Participant Information
        </Typography>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Full Name"
              value={registrant?.fullName || ''}
              onChange={(e) => handleChange('fullName', e.target.value)}
              fullWidth
              required
              size="small"
              inputProps={{ maxLength: MAX_INPUT_LENGTH }}
              sx={textFieldStyle}
            />
            <TextField
              label="Preferred Name"
              value={registrant?.preferredName || ''}
              onChange={(e) => handleChange('preferredName', e.target.value)}
              fullWidth
              size="small"
              inputProps={{ maxLength: MAX_INPUT_LENGTH }}
              sx={textFieldStyle}
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Date of Birth"
              type="date"
              value={registrant?.dateOfBirth ? String(registrant.dateOfBirth).split('T')[0] : ''}
              onChange={(e) => handleChange('dateOfBirth', e.target.value, (v) => v)}
              fullWidth
              required
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={textFieldStyle}
            />
            <TextField
              label="Age"
              value={calculateAge(registrant?.dateOfBirth) || ''}
              fullWidth
              size="small"
              disabled
              sx={textFieldStyle}
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Gender"
              value={registrant?.gender || ''}
              onChange={(e) => handleChange('gender', e.target.value)}
              fullWidth
              size="small"
              inputProps={{ maxLength: 50 }}
              sx={textFieldStyle}
            />
            <TextField
              label="Pronouns"
              value={getPronouns(registrant?.prounouns) || ''}
              onChange={(e) => handleChange('prounouns', e.target.value)}
              fullWidth
              size="small"
              inputProps={{ maxLength: 50 }}
              sx={textFieldStyle}
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Dancer Email"
              type="email"
              value={registrant?.dancerEmail || ''}
              onChange={(e) => handleChange('dancerEmail', e.target.value, sanitizeEmail)}
              fullWidth
              size="small"
              inputProps={{ maxLength: MAX_EMAIL_LENGTH }}
              sx={textFieldStyle}
            />
            <TextField
              label="Dancer Phone"
              value={registrant?.dancerPhoneNumber || ''}
              onChange={(e) => handleChange('dancerPhoneNumber', e.target.value, sanitizePhone)}
              fullWidth
              size="small"
              inputProps={{ maxLength: MAX_PHONE_LENGTH }}
              sx={textFieldStyle}
            />
          </Stack>
        </Stack>
      </Box>

      {/* Guardian Information */}
      <Box sx={{ flexShrink: 0 }}>
        <Typography variant="subtitle2" sx={{ color: COLORS.accent, mb: 2, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
          Guardian Information
        </Typography>
        <Stack spacing={2}>
          <TextField
            label="Guardian Full Name"
            value={registrant?.guardianFullName || ''}
            onChange={(e) => handleChange('guardianFullName', e.target.value)}
            fullWidth
            size="small"
            inputProps={{ maxLength: MAX_INPUT_LENGTH }}
            sx={textFieldStyle}
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Guardian Phone"
              value={registrant?.guardianPhoneNumber || ''}
              onChange={(e) => handleChange('guardianPhoneNumber', e.target.value, sanitizePhone)}
              fullWidth
              size="small"
              inputProps={{ maxLength: MAX_PHONE_LENGTH }}
              sx={textFieldStyle}
            />
            <TextField
              label="Guardian Email"
              type="email"
              value={registrant?.guardianEmail || ''}
              onChange={(e) => handleChange('guardianEmail', e.target.value, sanitizeEmail)}
              fullWidth
              size="small"
              inputProps={{ maxLength: MAX_EMAIL_LENGTH }}
              sx={textFieldStyle}
            />
          </Stack>
        </Stack>
      </Box>

      {/* Emergency Contact */}
      <Box sx={{ flexShrink: 0 }}>
        <Typography variant="subtitle2" sx={{ color: COLORS.success, mb: 2, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
          Emergency Contact
        </Typography>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Emergency Contact Name"
              value={registrant?.emergencyContactName || ''}
              onChange={(e) => handleChange('emergencyContactName', e.target.value)}
              fullWidth
              size="small"
              inputProps={{ maxLength: MAX_INPUT_LENGTH }}
              sx={textFieldStyle}
            />
            <TextField
              label="Relationship"
              value={registrant?.emergencyContactRelationship || ''}
              onChange={(e) => handleChange('emergencyContactRelationship', e.target.value)}
              fullWidth
              size="small"
              inputProps={{ maxLength: 100 }}
              sx={textFieldStyle}
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Emergency Contact Phone"
              value={registrant?.emergencyContactPhoneNumber || ''}
              onChange={(e) => handleChange('emergencyContactPhoneNumber', e.target.value, sanitizePhone)}
              fullWidth
              size="small"
              inputProps={{ maxLength: MAX_PHONE_LENGTH }}
              sx={textFieldStyle}
            />
            <TextField
              label="Emergency Contact Email"
              type="email"
              value={registrant?.emergencyContactEmail || ''}
              onChange={(e) => handleChange('emergencyContactEmail', e.target.value, sanitizeEmail)}
              fullWidth
              size="small"
              inputProps={{ maxLength: MAX_EMAIL_LENGTH }}
              sx={textFieldStyle}
            />
          </Stack>
        </Stack>
      </Box>

      {/* Medical Information */}
      <Box sx={{ flexShrink: 0 }}>
        <Typography variant="subtitle2" sx={{ color: COLORS.error, mb: 2, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
          Medical Information
        </Typography>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Allergies"
              value={registrant?.allergies || ''}
              onChange={(e) => handleChange('allergies', e.target.value)}
              fullWidth
              size="small"
              inputProps={{ maxLength: 500 }}
              sx={textFieldStyle}
            />
            <TextField
              label="Prescribed Medications"
              value={registrant?.prescribedMedications || ''}
              onChange={(e) => handleChange('prescribedMedications', e.target.value)}
              fullWidth
              size="small"
              inputProps={{ maxLength: 500 }}
              sx={textFieldStyle}
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              label="OTC Medication"
              value={registrant?.OTCMedication || ''}
              onChange={(e) => handleChange('OTCMedication', e.target.value)}
              fullWidth
              size="small"
              inputProps={{ maxLength: 500 }}
              sx={textFieldStyle}
            />
            <TextField
              label="Medical Conditions"
              value={registrant?.medicalConditions || ''}
              onChange={(e) => handleChange('medicalConditions', e.target.value)}
              fullWidth
              size="small"
              inputProps={{ maxLength: 500 }}
              sx={textFieldStyle}
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Physician Name"
              value={registrant?.physicianName || ''}
              onChange={(e) => handleChange('physicianName', e.target.value)}
              fullWidth
              size="small"
              inputProps={{ maxLength: MAX_INPUT_LENGTH }}
              sx={textFieldStyle}
            />
            <TextField
              label="Physician Phone"
              value={registrant?.physicianPhoneNumber || ''}
              onChange={(e) => handleChange('physicianPhoneNumber', e.target.value, sanitizePhone)}
              fullWidth
              size="small"
              inputProps={{ maxLength: MAX_PHONE_LENGTH }}
              sx={textFieldStyle}
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Health Insurance Provider"
              value={registrant?.healthInsuranceProvider || ''}
              onChange={(e) => handleChange('healthInsuranceProvider', e.target.value)}
              fullWidth
              size="small"
              inputProps={{ maxLength: MAX_INPUT_LENGTH }}
              sx={textFieldStyle}
            />
            <TextField
              label="Policy Number"
              value={registrant?.policyNumber || ''}
              onChange={(e) => handleChange('policyNumber', e.target.value)}
              fullWidth
              size="small"
              inputProps={{ maxLength: MAX_INPUT_LENGTH }}
              sx={textFieldStyle}
            />
          </Stack>
        </Stack>
      </Box>

      {/* RDA Affiliation */}
      <Box sx={{ flexShrink: 0 }}>
        <Typography variant="subtitle2" sx={{ color: COLORS.accent, mb: 2, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
          RDA Affiliation
        </Typography>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <TextField
              label="RDA Member (Yes/No)"
              value={registrant?.RDAMember || ''}
              onChange={(e) => handleChange('RDAMember', e.target.value)}
              fullWidth
              size="small"
              inputProps={{ maxLength: 10 }}
              sx={textFieldStyle}
            />
            <TextField
              label="RDA Region"
              value={typeof registrant?.RDARegion === 'object' ? registrant?.RDARegion?.label || '' : registrant?.RDARegion || ''}
              onChange={(e) => handleChange('RDARegion', e.target.value)}
              fullWidth
              size="small"
              inputProps={{ maxLength: MAX_INPUT_LENGTH }}
              sx={textFieldStyle}
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              label="RDA Dance School/Company"
              value={typeof registrant?.currentRDADanceSchoolCompany === 'object' ? registrant?.currentRDADanceSchoolCompany?.label || '' : registrant?.currentRDADanceSchoolCompany || ''}
              onChange={(e) => handleChange('currentRDADanceSchoolCompany', e.target.value)}
              fullWidth
              size="small"
              inputProps={{ maxLength: MAX_INPUT_LENGTH }}
              sx={textFieldStyle}
            />
            <TextField
              label="Non-RDA Dance School/Company"
              value={registrant?.currentDanceSchoolCompany || ''}
              onChange={(e) => handleChange('currentDanceSchoolCompany', e.target.value)}
              fullWidth
              size="small"
              inputProps={{ maxLength: MAX_INPUT_LENGTH }}
              sx={textFieldStyle}
            />
          </Stack>
        </Stack>
      </Box>

      {/* Address Information */}
      <Box sx={{ flexShrink: 0 }}>
        <Typography variant="subtitle2" sx={{ color: COLORS.accent, mb: 2, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
          Address Information
        </Typography>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Participant Street Address"
              value={registrant?.participantStreetAddress || ''}
              onChange={(e) => handleChange('participantStreetAddress', e.target.value)}
              fullWidth
              size="small"
              inputProps={{ maxLength: MAX_INPUT_LENGTH }}
              sx={textFieldStyle}
            />
            <TextField
              label="Participant City/State/Zip"
              value={registrant?.participantCityStateZipcode || ''}
              onChange={(e) => handleChange('participantCityStateZipcode', e.target.value)}
              fullWidth
              size="small"
              inputProps={{ maxLength: MAX_INPUT_LENGTH }}
              sx={textFieldStyle}
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Parent Street Address"
              value={registrant?.parentStreetAddress || ''}
              onChange={(e) => handleChange('parentStreetAddress', e.target.value)}
              fullWidth
              size="small"
              inputProps={{ maxLength: MAX_INPUT_LENGTH }}
              sx={textFieldStyle}
            />
            <TextField
              label="Parent City/State/Zip"
              value={registrant?.parentCityStateZipcode || ''}
              onChange={(e) => handleChange('parentCityStateZipcode', e.target.value)}
              fullWidth
              size="small"
              inputProps={{ maxLength: MAX_INPUT_LENGTH }}
              sx={textFieldStyle}
            />
          </Stack>
        </Stack>
      </Box>

      {/* Housing & Logistics */}
      <Box sx={{ flexShrink: 0 }}>
        <Typography variant="subtitle2" sx={{ color: COLORS.accent, mb: 2, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
          Housing & Logistics
        </Typography>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Housing Required"
              value={registrant?.housingCheck || ''}
              onChange={(e) => handleChange('housingCheck', e.target.value)}
              fullWidth
              size="small"
              inputProps={{ maxLength: 10 }}
              sx={textFieldStyle}
            />
            <TextField
              label="Preferred Roommate"
              value={registrant?.preferredRoommate || ''}
              onChange={(e) => handleChange('preferredRoommate', e.target.value)}
              fullWidth
              size="small"
              inputProps={{ maxLength: MAX_INPUT_LENGTH }}
              sx={textFieldStyle}
            />
          </Stack>
          <TextField
            label="Dietary Restrictions"
            value={registrant?.dietaryRestrictions || ''}
            onChange={(e) => handleChange('dietaryRestrictions', e.target.value)}
            fullWidth
            size="small"
            inputProps={{ maxLength: 500 }}
            sx={textFieldStyle}
          />
        </Stack>
      </Box>

      {/* Merchandise - Event Specific */}
      {/* {currentMerch && (
        <Box sx={{ flexShrink: 0 }}>
          <Typography variant="subtitle2" sx={{ color: COLORS.warning, mb: 2, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
            Merchandise - {currentMerch.label}
          </Typography>
          <Stack spacing={2}>
            <Stack direction="row" spacing={2}>
              <TextField
                label={`${currentMerch.label} T-Shirt Size`}
                value={registrant?.[currentMerch.sizeKey] || ''}
                onChange={(e) => handleChange(currentMerch.sizeKey, e.target.value)}
                fullWidth
                size="small"
                inputProps={{ maxLength: 20 }}
                sx={textFieldStyle}
              />
              <TextField
                label={`${currentMerch.label} T-Shirt Qty`}
                value={registrant?.[currentMerch.qtyKey] || ''}
                onChange={(e) => handleChange(currentMerch.qtyKey, e.target.value)}
                fullWidth
                size="small"
                inputProps={{ maxLength: 10 }}
                sx={textFieldStyle}
              />
            </Stack>
          </Stack>
        </Box>
      )} */}
      {currentMerch && (
  <Box sx={{ flexShrink: 0 }}>
    <Typography variant="subtitle2" sx={{ color: COLORS.warning, mb: 2, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
      Merchandise - {currentMerch.label}
    </Typography>
    <Stack spacing={3}>
      {currentMerch.items.map((item, index) => (
        <Box key={index}>
          <Typography variant="caption" sx={{ color: COLORS.textSecondary, fontWeight: 700, mb: 1, display: 'block' }}>
            {item.type.toUpperCase()}
          </Typography>
          <Stack direction="row" spacing={2}>
            <TextField
              label={`${item.type} Size`}
              value={registrant?.[item.sizeKey] || ''}
              onChange={(e) => handleChange(item.sizeKey, e.target.value)}
              fullWidth
              size="small"
              inputProps={{ maxLength: 20 }}
              sx={textFieldStyle}
            />
            <TextField
              label={`${item.type} Qty`}
              value={registrant?.[item.qtyKey] || ''}
              onChange={(e) => handleChange(item.qtyKey, e.target.value)}
              fullWidth
              size="small"
              inputProps={{ maxLength: 10 }}
              sx={textFieldStyle}
            />
          </Stack>
        </Box>
      ))}
    </Stack>
  </Box>
)}
    </Stack>
  );
};

// Info row component
const InfoRow = ({ label, value, icon: Icon }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, py: 1.5 }}>
    {Icon && <Icon sx={{ color: COLORS.accent, fontSize: 20, mt: 0.3 }} aria-hidden="true" />}
    <Box sx={{ flex: 1 }}>
      <Typography variant="caption" sx={{ color: COLORS.textSecondary, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="body1" sx={{ color: COLORS.textPrimary, fontWeight: 500 }}>
        {value || '-'}
      </Typography>
    </Box>
  </Box>
);

// Contact card component
const ContactCard = ({ title, icon: Icon, iconColor, fields }) => {
  const hasData = fields.some(f => f.value);
  if (!hasData) return null;

  return (
    <Paper sx={{
      bgcolor: COLORS.card,
      border: `1px solid ${COLORS.border}`,
      borderRadius: '12px',
      p: 2.5,
      flex: 1
    }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <Icon sx={{ color: iconColor, fontSize: 20 }} aria-hidden="true" />
        <Typography variant="subtitle2" fontWeight={600} sx={{ color: iconColor }}>
          {title}
        </Typography>
      </Stack>
      <Stack spacing={1}>
        {fields.map((field, idx) => (
          <Box key={idx}>
            <Typography variant="caption" sx={{ color: COLORS.textSecondary, fontSize: '0.65rem', textTransform: 'uppercase' }}>
              {field.label}
            </Typography>
            <Typography variant="body2" fontWeight={field.bold ? 500 : 400} sx={{color: COLORS.textPrimary}}>
              {field.value || '-'}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Paper>
  );
};

const DancerDetail = () => {
  const { eventId, dancerId } = useParams();
  const navigate = useNavigate();
  const [registrant, setRegistrant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Edit dialog state
  const [editRegistrantDialogOpen, setEditRegistrantDialogOpen] = useState(false);
  const [editingRegistrant, setEditingRegistrant] = useState(null);

//   const fetchData = useCallback(async (signal) => {
//     try {
//       setLoading(true);
//       setError(null);

//       const response = await fetch(API_URL, { signal });
//       if (!response.ok) throw new Error(`Server error: ${response.status}`);

//       const data = await response.json();
//       if (!Array.isArray(data)) throw new Error('Invalid data format');

//       // Find the registrant by ID 
//       //dancer id needs to be unique id saved in database  
//       const found = data.find(r => r.unique_id === parseInt(dancerId, 10));
//       if (!found) {
//         throw new Error('Registrant not found');
//       }

//       setRegistrant(found);
//     } catch (err) {
//       if (err.name !== 'AbortError') {
//         setError(err.message);
//         console.error('Error fetching registrant:', err);
//       }
//     } finally {
//       setLoading(false);
//     }
//   }, [dancerId]);

//     const fetchData = useCallback(async (signal) => {
//   try {
//     setLoading(true);
//     setError(null);

//     const response = await fetch(API_URL, { signal });
//     if (!response.ok) throw new Error(`Server error: ${response.status}`);

//     const data = await response.json();
    
//     const found = data.find(r => String(r.unique_id) === String(dancerId));

//     if (!found) {
//       throw new Error('Registrant not found');
//     }

//     setRegistrant(found);
//   } catch (err) {
//     if (err.name !== 'AbortError') {
//       setError(err.message);
//     }
//   } finally {
//     setLoading(false);
//   }
// }, [dancerId]);

  const fetchData = useCallback(async (signal) => {
  try {
    setLoading(true);
    setError(null);

    const token = localStorage.getItem('token');

    const response = await fetch(API_URL, { 
      signal,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      if (response.status === 403) throw new Error("Access Denied: Session expired or unauthorized");
      throw new Error(`Server error: ${response.status}`);
    }

    const data = await response.json();
    
    const records = Array.isArray(data) ? data : [data];
    const found = records.find(r => String(r.unique_id) === String(dancerId));

    if (!found) {
      throw new Error('Registrant not found');
    }

    setRegistrant(found);
  } catch (err) {
    if (err.name !== 'AbortError') {
      setError(err.message);
    }
  } finally {
    setLoading(false);
  }
}, [dancerId]);

  useEffect(() => {
    const controller = new AbortController();
    fetchData(controller.signal);
    return () => controller.abort();
  }, [fetchData]);

  const handleBack = useCallback(() => {
    navigate('/admin/events', { state: { selectedEventId: eventId } });
  }, [navigate, eventId]);

  const openEditRegistrantDialog = useCallback(() => {
    if (registrant) {
      setEditingRegistrant({ ...registrant });
      setEditRegistrantDialogOpen(true);
    }
  }, [registrant]);


  const handleEditRegistrant = useCallback(async () => {
    console.log("HandleEdit triggered with:", editingRegistrant);

    const registrantId = editingRegistrant?.unique_id || editingRegistrant?.id;

    if (!registrantId || !editingRegistrant?.fullName?.trim()) {
      console.warn("Missing ID or Name - aborting update");
      return;
    }

    try {
      const response = await fetch(`${API_URL}/${registrantId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify(editingRegistrant)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update registrant');
      }

      setRegistrant({ ...editingRegistrant });
      

      setEditRegistrantDialogOpen(false);
      setEditingRegistrant(null);
      
      console.log("Update successful");

    } catch (err) {
      console.error('Error updating registrant:', err);
      setError(err.message);
    }
  }, [editingRegistrant]);

  // Get event display info
  const eventInfo = EVENT_METADATA[eventId] || { title: eventId, date: '' };
  const role = registrant?.[eventId] || '';

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: COLORS.bg }} role="status" aria-label="Loading">
        <CircularProgress sx={{ color: COLORS.accent }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4, bgcolor: COLORS.bg, minHeight: '100vh', color: COLORS.textPrimary }}>
        <Alert severity="error" sx={{ mb: 3, borderRadius: '10px' }} role="alert">
          {error}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/events')}
          sx={{ color: COLORS.textPrimary, textTransform: 'none' }}
        >
          Back to Events
        </Button>
      </Box>
    );
  }

  const handleDownloadFile = async (fileName, type = 'waiver') => {
  const token = localStorage.getItem('token');
  const endpoint = type === 'waiver' ? 'download-waiver' : 'download-medical';
  const url = `${API_BASE_URL}/api/${endpoint}/${dancerId}/${encodeURIComponent(fileName)}`;


  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        // Waivers might ignore this, but Medical NEEDS it
        'Authorization': `Bearer ${token}` 
      }
    });

    if (response.status === 401) {
      alert("Session expired. Please log in again to view medical records.");
      return;
    }

    if (!response.ok) throw new Error("File not found");

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(blobUrl);
  } catch (err) {
    console.error("Download failed:", err);
  }
};

  return (
    <Box sx={{ bgcolor: COLORS.bg, minHeight: '100vh', color: COLORS.textPrimary, p: 4 }}>
      <Box sx={{ maxWidth: 900, mx: 'auto' }}>
        {/* Back Button */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          size="small"
          sx={{
            color: COLORS.textSecondary,
            mb: 3,
            '&:hover': { bgcolor: 'rgba(255,255,255,0.05)', color: COLORS.textPrimary },
            textTransform: 'none',
            fontSize: '0.85rem'
          }}
        >
          Back to {eventInfo.title}
        </Button>

        {/* Header Card */}
        <Paper sx={{
          bgcolor: COLORS.card,
          border: `1px solid ${COLORS.border}`,
          borderRadius: '16px',
          p: 3,
          mb: 3
        }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} mb={2}>
            <Box sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              bgcolor: COLORS.accent,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <PersonIcon sx={{ fontSize: 32, color: COLORS.textPrimary }} aria-hidden="true" />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" component="h1" fontWeight={700}>
                {registrant?.fullName || 'Unknown'}
              </Typography>
              {registrant?.preferredName && (
                <Typography variant="body1" sx={{ color: COLORS.textSecondary }}>
                  Goes by "{registrant.preferredName}"
                </Typography>
              )}
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
              {role && (
                <Chip
                  label={role}
                  size="small"
                  sx={{
                    bgcolor: role === 'Choreographer' ? 'rgba(255, 159, 10, 0.1)' : 'rgba(108, 93, 211, 0.1)',
                    color: role === 'Choreographer' ? COLORS.warning : COLORS.accent,
                    fontWeight: 600
                  }}
                />
              )}
              <Chip
                label={eventInfo.title}
                size="small"
                sx={{
                  bgcolor: 'rgba(52, 199, 89, 0.1)',
                  color: COLORS.success,
                  fontWeight: 600
                }}
              />
              <Button
                startIcon={<EditIcon />}
                onClick={openEditRegistrantDialog}
                size="small"
                sx={{
                  color: COLORS.textPrimary,
                  borderColor: COLORS.border,
                  border: '1px solid',
                  '&:hover': { borderColor: COLORS.accent, bgcolor: 'rgba(108, 93, 211, 0.1)' },
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  ml: 1
                }}
              >
                Edit
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {/* Personal Information */}
        <Paper sx={{
          bgcolor: COLORS.card,
          border: `1px solid ${COLORS.border}`,
          borderRadius: '16px',
          p: 3,
          mb: 3
        }}>
          <Typography variant="h6" component="h2" fontWeight={600} sx={{ mb: 2, color: COLORS.accent }}>
            Personal Information
          </Typography>
          <Divider sx={{ borderColor: COLORS.border, mb: 2 }} />

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <InfoRow label="Full Name" value={registrant?.fullName} icon={PersonIcon} />
            <InfoRow label="Preferred Name" value={registrant?.preferredName} />
            <InfoRow
              label="Date of Birth"
              value={registrant?.dateOfBirth ? new Date(registrant.dateOfBirth).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : null}
              icon={CakeIcon}
            />
            <InfoRow label="Age" value={calculateAge(registrant?.dateOfBirth)} />
            <InfoRow label="Gender" value={registrant?.gender} />
            <InfoRow label="Pronouns" value={getPronouns(registrant?.prounouns)} />
          </Box>
        </Paper>

        {/* Contact Information */}
        <Paper sx={{
          bgcolor: COLORS.card,
          border: `1px solid ${COLORS.border}`,
          borderRadius: '16px',
          p: 3,
          mb: 3
        }}>
          <Typography variant="h6" component="h2" fontWeight={600} sx={{ mb: 2, color: COLORS.accent }}>
            Contact Information
          </Typography>
          <Divider sx={{ borderColor: COLORS.border, mb: 2 }} />

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <InfoRow label="Dancer Email" value={registrant?.dancerEmail} icon={EmailIcon} />
            <InfoRow label="Dancer Phone" value={registrant?.dancerPhoneNumber} icon={PhoneIcon} />
          </Box>
        </Paper>

        {/* Guardian & Emergency Contacts */}
        <Typography variant="h6" component="h2" fontWeight={600} sx={{ mb: 2, color: COLORS.accent }}>
          Guardian & Emergency Contacts
        </Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={3}>
          <ContactCard
            title="Guardian"
            icon={GuardianIcon}
            iconColor={COLORS.accent}
            fields={[
              { label: 'Name', value: registrant?.guardianFullName, bold: true },
              { label: 'Phone', value: registrant?.guardianPhoneNumber },
              { label: 'Email', value: registrant?.guardianEmail }
            ]}
          />
          <ContactCard
            title="Emergency Contact"
            icon={ContactPhoneIcon}
            iconColor={COLORS.success}
            fields={[
              { label: 'Name', value: registrant?.emergencyContactName, bold: true },
              { label: 'Relationship', value: registrant?.emergencyContactRelationship },
              { label: 'Phone', value: registrant?.emergencyContactPhoneNumber },
              { label: 'Email', value: registrant?.emergencyContactEmail }
            ]}
          />
        </Stack>

        {/* No contacts message */}
        {!registrant?.guardianFullName && !registrant?.emergencyContactName && (
          <Paper sx={{
            bgcolor: COLORS.card,
            border: `1px solid ${COLORS.border}`,
            borderRadius: '12px',
            p: 3,
            textAlign: 'center',
            mb: 3
          }}>
            <Typography sx={{ color: COLORS.textSecondary }}>
              No guardian or emergency contacts on file
            </Typography>
          </Paper>
        )}

        {/* Medical Information */}
        {(registrant?.allergies || registrant?.prescribedMedications || registrant?.medicalConditions || registrant?.physicianName || registrant?.OTCMedication || registrant?.healthInsuranceProvider) && (
          <Paper sx={{
            bgcolor: COLORS.card,
            border: `1px solid ${COLORS.border}`,
            borderRadius: '16px',
            p: 3,
            mb: 3
          }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <MedicalIcon sx={{ color: COLORS.error, fontSize: 22 }} aria-hidden="true" />
              <Typography variant="h6" component="h2" fontWeight={600} sx={{ color: COLORS.error }}>
                Medical Information
              </Typography>
            </Stack>
            <Divider sx={{ borderColor: COLORS.border, mb: 2 }} />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <InfoRow label="Allergies" value={registrant?.allergies} />
              <InfoRow label="Prescribed Medications" value={registrant?.prescribedMedications} />
              <InfoRow label="OTC Medication" value={registrant?.OTCMedication} />
              <InfoRow label="Medical Conditions" value={registrant?.medicalConditions} />
              <InfoRow label="Physician Name" value={registrant?.physicianName} />
              <InfoRow label="Physician Phone" value={registrant?.physicianPhoneNumber} />
              <InfoRow label="Health Insurance Provider" value={registrant?.healthInsuranceProvider} />
              <InfoRow label="Policy Number" value={registrant?.policyNumber} />
            </Box>
          </Paper>
        )}

        {/* RDA Affiliation */}
        {(registrant?.RDAMember || registrant?.RDARegion || registrant?.currentRDADanceSchoolCompany || registrant?.currentDanceSchoolCompany) && (
          <Paper sx={{
            bgcolor: COLORS.card,
            border: `1px solid ${COLORS.border}`,
            borderRadius: '16px',
            p: 3,
            mb: 3
          }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <SchoolIcon sx={{ color: COLORS.accent, fontSize: 22 }} aria-hidden="true" />
              <Typography variant="h6" component="h2" fontWeight={600} sx={{ color: COLORS.accent }}>
                RDA Affiliation
              </Typography>
            </Stack>
            <Divider sx={{ borderColor: COLORS.border, mb: 2 }} />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <InfoRow label="RDA Member" value={registrant?.RDAMember} />
              <InfoRow label="RDA Region" value={typeof registrant?.RDARegion === 'object' ? registrant?.RDARegion?.label : registrant?.RDARegion} />
              <InfoRow label="RDA Dance School/Company" value={typeof registrant?.currentRDADanceSchoolCompany === 'object' ? registrant?.currentRDADanceSchoolCompany?.label : registrant?.currentRDADanceSchoolCompany} />
              <InfoRow label="Non-RDA Dance School/Company" value={registrant?.currentDanceSchoolCompany} />
            </Box>
          </Paper>
        )}

        {/* Address Information */}
        {(registrant?.participantStreetAddress || registrant?.parentStreetAddress) && (
          <Paper sx={{
            bgcolor: COLORS.card,
            border: `1px solid ${COLORS.border}`,
            borderRadius: '16px',
            p: 3,
            mb: 3
          }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <HomeIcon sx={{ color: COLORS.accent, fontSize: 22 }} aria-hidden="true" />
              <Typography variant="h6" component="h2" fontWeight={600} sx={{ color: COLORS.accent }}>
                Address Information
              </Typography>
            </Stack>
            <Divider sx={{ borderColor: COLORS.border, mb: 2 }} />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <InfoRow label="Participant Street Address" value={registrant?.participantStreetAddress} />
              <InfoRow label="Participant City/State/Zip" value={registrant?.participantCityStateZipcode} />
              <InfoRow label="Parent/Guardian Street Address" value={registrant?.parentStreetAddress} />
              <InfoRow label="Parent/Guardian City/State/Zip" value={registrant?.parentCityStateZipcode} />
            </Box>
          </Paper>
        )}

        {/* DCI Observation & Tickets */}
        {(() => {
          const dciConfig = {
            DCIRosewell26: {
              label: 'Rosewell DCI',
              fullDayKey: 'fullDayPassesRosewell26',
              choreographyKey: 'choreographyPassesRosewell26',
              guestKey: 'guestTicketsRosewell26'
            },
            DCILawrence26: {
              label: 'Lawrence DCI',
              fullDayKey: 'fullDayPassesLawrence26',
              choreographyKey: 'choreographyPassesLawrence26',
              guestKey: 'guestTicketsLawrence26'
            },
            DCICarlisle26: {
              label: 'Carlisle DCI',
              fullDayKey: 'fullDayPassesCarlisle26',
              choreographyKey: 'choreographyPassesCarlisle26',
              guestKey: 'guestTicketsCarlisle26'
            }
          };
          const config = dciConfig[eventId];
          if (!config || (!registrant?.[config.fullDayKey] && !registrant?.[config.choreographyKey] && !registrant?.[config.guestKey])) return null;

          return (
            <Paper sx={{
              bgcolor: COLORS.card,
              border: `1px solid ${COLORS.border}`,
              borderRadius: '16px',
              p: 3,
              mb: 3
            }}>
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <LocalActivityIcon sx={{ color: COLORS.success, fontSize: 22 }} aria-hidden="true" />
                <Typography variant="h6" component="h2" fontWeight={600} sx={{ color: COLORS.success }}>
                  DCI Observation & Tickets - {config.label}
                </Typography>
              </Stack>
              <Divider sx={{ borderColor: COLORS.border, mb: 2 }} />

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
                <InfoRow label="Full Day Observer Passes ($50 each)" value={registrant?.[config.fullDayKey]} />
                <InfoRow label="Choreography Showing Only ($10 each)" value={registrant?.[config.choreographyKey]} />
                <InfoRow label="Guest Tickets ($10 each)" value={registrant?.[config.guestKey]} />
              </Box>
            </Paper>
          );
        })()}

        {/* Merchandise - Shows only for current event */}
        {/* {(() => {
          const merchConfig = {
            DCIRosewell26: { sizeKey: 'RosewellDCITShirtSize', qtyKey: 'RosewellDCITShirtQuantity', label: 'Rosewell DCI' },
            DCILawrence26: { sizeKey: 'LawrenceDCITShirtSize', qtyKey: 'LawrenceDCITShirtQuantity', label: 'Lawrence DCI' },
            DCICarlisle26: { sizeKey: 'CarlisleDCITShirtSize', qtyKey: 'CarlisleDCITShirtQuantity', label: 'Carlisle DCI' },
            PWKingwood26: { sizeKey: 'KingwoodPWTShirtSize', qtyKey: 'KingwoodPWTShirtQuantity', label: 'Kingwood PW' }
          };
          const config = merchConfig[eventId];
          if (!config || (!registrant?.[config.sizeKey] && !registrant?.[config.qtyKey])) return null;

          return (
            <Paper sx={{
              bgcolor: COLORS.card,
              border: `1px solid ${COLORS.border}`,
              borderRadius: '16px',
              p: 3,
              mb: 3
            }}>
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <MerchIcon sx={{ color: COLORS.warning, fontSize: 22 }} aria-hidden="true" />
                <Typography variant="h6" component="h2" fontWeight={600} sx={{ color: COLORS.warning }}>
                  Merchandise - {config.label}
                </Typography>
              </Stack>
              <Divider sx={{ borderColor: COLORS.border, mb: 2 }} />

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <InfoRow label="T-Shirt Size" value={registrant?.[config.sizeKey]} />
                <InfoRow label="T-Shirt Quantity" value={registrant?.[config.qtyKey]} />
              </Box>
            </Paper>
          );
        })()} */}

        {(() => {
  const merchConfig = {
    DCIRosewell26: { 
      label: 'Rosewell DCI',
      items: [
        { type: 'T-Shirt', sizeKey: 'RosewellDCITShirtSize', qtyKey: 'RosewellDCITShirtQuantity' },
        { type: 'Sweatshirt', sizeKey: 'RosewellDCISweatShirtSize', qtyKey: 'RosewellDCISweatShirtQuantity' }
      ]
    },
    DCILawrence26: { 
      label: 'Lawrence DCI',
      items: [
        { type: 'T-Shirt', sizeKey: 'LawrenceDCITShirtSize', qtyKey: 'LawrenceDCITShirtQuantity' },
        { type: 'Sweatshirt', sizeKey: 'LawrenceDCISweatShirtSize', qtyKey: 'LawrenceDCISweatShirtQuantity' }
      ]
    },
    DCICarlisle26: { 
      label: 'Carlisle DCI',
      items: [
        { type: 'T-Shirt', sizeKey: 'CarlisleDCITShirtSize', qtyKey: 'CarlisleDCITShirtQuantity' },
        { type: 'Sweatshirt', sizeKey: 'CarlisleDCISweatShirtSize', qtyKey: 'CarlisleDCISweatShirtQuantity' }
      ]
    },
    PWKingwood26: { 
      label: 'Kingwood PW',
      items: [
        { type: 'T-Shirt', sizeKey: 'KingwoodPWTShirtSize', qtyKey: 'KingwoodPWTShirtQuantity' },
        { type: 'Sweatshirt', sizeKey: 'KingwoodPWSweatShirtSize', qtyKey: 'KingwoodPWSweatShirtQuantity' }
      ]
    }
  };

  const config = merchConfig[eventId];
  
  // Check if ANY items have data. If no t-shirt AND no sweatshirt, hide the section.
  const hasMerch = config?.items.some(item => registrant?.[item.sizeKey] || registrant?.[item.qtyKey]);
  
  if (!config || !hasMerch) return null;

  return (
    <Paper sx={{
      bgcolor: COLORS.card,
      border: `1px solid ${COLORS.border}`,
      borderRadius: '16px',
      p: 3,
      mb: 3
    }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <MerchIcon sx={{ color: COLORS.warning, fontSize: 22 }} aria-hidden="true" />
        <Typography variant="h6" component="h2" fontWeight={600} sx={{ color: COLORS.warning }}>
          Merchandise - {config.label}
        </Typography>
      </Stack>
      <Divider sx={{ borderColor: COLORS.border, mb: 2 }} />

      {/* Loop through each item type (T-shirt, Sweatshirt) */}
      <Stack spacing={3}>
        {config.items.map((item, idx) => {
          const size = registrant?.[item.sizeKey];
          const qty = registrant?.[item.qtyKey];

          // Skip rendering this specific item if it wasn't ordered
          if (!size && !qty) return null;

          return (
            <Box key={idx}>
              <Typography variant="subtitle2" sx={{ color: COLORS.textSecondary, mb: 1, fontWeight: 700 }}>
                {item.type.toUpperCase()}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <InfoRow label="Size" value={size || 'None'} />
                <InfoRow label="Quantity" value={qty || '0'} />
              </Box>
              {idx < config.items.length - 1 && <Divider sx={{ mt: 2, borderStyle: 'dashed', opacity: 0.5 }} />}
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
})()}

        {/* Housing & Logistics */}
        {(registrant?.housingCheck || registrant?.preferredRoommate || registrant?.dietaryRestrictions) && (
          <Paper sx={{
            bgcolor: COLORS.card,
            border: `1px solid ${COLORS.border}`,
            borderRadius: '16px',
            p: 3,
            mb: 3
          }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <HomeIcon sx={{ color: COLORS.accent, fontSize: 22 }} aria-hidden="true" />
              <Typography variant="h6" component="h2" fontWeight={600} sx={{ color: COLORS.accent }}>
                Housing & Logistics
              </Typography>
            </Stack>
            <Divider sx={{ borderColor: COLORS.border, mb: 2 }} />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <InfoRow label="Housing Required" value={registrant?.housingCheck} />
              <InfoRow label="Preferred Roommate" value={registrant?.preferredRoommate} />
              <InfoRow label="Dietary Restrictions" value={registrant?.dietaryRestrictions} />
            </Box>
          </Paper>
        )}

        {/* Signatures (Display Only - Secure) */}
        {(registrant?.printedSignName || registrant?.parentSignName) && (
          <Paper sx={{
            bgcolor: COLORS.card,
            border: `1px solid ${COLORS.border}`,
            borderRadius: '16px',
            p: 3,
            mb: 3
          }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <PersonIcon sx={{ color: COLORS.textSecondary, fontSize: 22 }} aria-hidden="true" />
              <Typography variant="h6" component="h2" fontWeight={600} sx={{ color: COLORS.textSecondary }}>
                Signatures
              </Typography>
            </Stack>
            <Divider sx={{ borderColor: COLORS.border, mb: 2 }} />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <InfoRow label="Signed By (Dancer)" value={registrant?.printedSignName} />
              <InfoRow label="Signed By (Guardian)" value={registrant?.parentSignName} />
            </Box>
          </Paper>
        )}

        {/* Authorized Pickup */}
        {registrant?.authorizedPeople && registrant.authorizedPeople.length > 0 && (
          <Paper sx={{
            bgcolor: COLORS.card,
            border: `1px solid ${COLORS.border}`,
            borderRadius: '16px',
            p: 3,
            mb: 3
          }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <GroupIcon sx={{ color: COLORS.success, fontSize: 22 }} aria-hidden="true" />
              <Typography variant="h6" component="h2" fontWeight={600} sx={{ color: COLORS.success }}>
                Authorized Pickup
              </Typography>
            </Stack>
            <Divider sx={{ borderColor: COLORS.border, mb: 2 }} />

            <Stack spacing={2}>
              {registrant.authorizedPeople.map((person, idx) => (
                <Box key={idx} sx={{ p: 2, bgcolor: COLORS.bg, borderRadius: '8px', border: `1px solid ${COLORS.border}` }}>
                  <Typography variant="subtitle2" sx={{ color: COLORS.textPrimary, fontWeight: 600, mb: 1 }}>
                    {person?.name || `Person ${idx + 1}`}
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 1 }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: COLORS.textSecondary, textTransform: 'uppercase', fontSize: '0.6rem' }}>Relationship</Typography>
                      <Typography variant="body2" sx={{color: COLORS.textPrimary}}>{person?.relationship || '-'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: COLORS.textSecondary, textTransform: 'uppercase', fontSize: '0.6rem' }}>Phone</Typography>
                      <Typography variant="body2" sx={{color: COLORS.textPrimary}}>{person?.phone || '-'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: COLORS.textSecondary, textTransform: 'uppercase', fontSize: '0.6rem' }}>Email</Typography>
                      <Typography variant="body2" sx={{color: COLORS.textPrimary}}>{person?.email || '-'}</Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Stack>
          </Paper>
        )}

        {/* Files & Documents Section */}
{registrant && (
  <Paper sx={{ p: 3, bgcolor: COLORS.card, borderRadius: '12px', border: `1px solid ${COLORS.border}`, mt: 3 }}>
    <Typography variant="h6" sx={{ color: COLORS.textPrimary, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
      <MedicalIcon sx={{ color: COLORS.accent }} /> Event Documents
    </Typography>
    
    <Stack spacing={2}>
      {registrant.fileManifest?.waivers && Object.keys(registrant.fileManifest.waivers)
        .filter(fileName => {
          const location = eventId.replace('DCI', '').replace('PW','').replace('26', '');
          return fileName.toLowerCase().includes(location.toLowerCase());
        })
        .map((fileName) => (
          <Box key={fileName} sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            p: 1.5, 
            bgcolor: 'rgba(255,255,255,0.03)', 
            borderRadius: '8px',
            border: `1px solid ${COLORS.border}`
          }}>
            <Stack>
              <Typography sx={{ color: COLORS.textPrimary, fontWeight: 500 }}>{fileName}</Typography>
              <Typography variant="caption" sx={{ color: COLORS.textSecondary }}>Waiver Agreement</Typography>
            </Stack>
            <Button 
              variant="contained" 
              size="small" 
              onClick={() => handleDownloadFile(fileName, 'waiver')}
              sx={{ bgcolor: COLORS.accent, '&:hover': { bgcolor: COLORS.accentHover } }}
            >
              Download
            </Button>
          </Box>
      ))}

      {registrant.fileManifest?.medicalCards && Object.keys(registrant.fileManifest.medicalCards).map((fileName) => (
        <Box key={fileName} sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          p: 1.5, 
          bgcolor: 'rgba(255,255,255,0.03)', 
          borderRadius: '8px',
          border: `1px solid ${COLORS.border}`
        }}>
          <Stack>
            <Typography sx={{ color: COLORS.textPrimary, fontWeight: 500 }}>{fileName}</Typography>
            <Typography variant="caption" sx={{ color: COLORS.textSecondary }}>Medical Insurance Card</Typography>
          </Stack>
          <Button 
            variant="contained" 
            size="small" 
            onClick={() => handleDownloadFile(fileName, 'medical')}
            sx={{ bgcolor: COLORS.success, '&:hover': { opacity: 0.8 } }}
          >
            Download
          </Button>
        </Box>
      ))}

      {(!registrant.fileManifest?.waivers || Object.keys(registrant.fileManifest.waivers).filter(f => {
          const loc = eventId.replace('DCI', '').replace('PW','').replace('26', '');
          return f.toLowerCase().includes(loc.toLowerCase());
        }).length === 0) && (
        <Typography sx={{ color: COLORS.textSecondary, fontStyle: 'italic', textAlign: 'center', py: 2 }}>
          No waivers found for the {eventId.replace('DCI', '').replace('26', '')} location.
        </Typography>
      )}
    </Stack>
  </Paper>
)}

      </Box>

      {/* EDIT REGISTRANT DIALOG */}
      <Dialog
        open={editRegistrantDialogOpen}
        onClose={() => { setEditRegistrantDialogOpen(false); setEditingRegistrant(null); }}
        maxWidth="md"
        fullWidth
        disableRestoreFocus
        aria-labelledby="edit-registrant-dialog-title"
        PaperProps={{
          sx: {
            bgcolor: COLORS.card,
            color: COLORS.textPrimary,
            borderRadius: '12px',
            border: `1px solid ${COLORS.border}`,
            maxHeight: '92vh',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle id="edit-registrant-dialog-title" sx={{ borderBottom: `1px solid ${COLORS.border}`, pb: 2, flexShrink: 0 }}>
          <Typography variant="h6" component="span" fontWeight={700} fontSize="1rem">Edit Registrant</Typography>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            p: 2.5,
            overflowY: 'auto',
            flex: '1 1 auto',
            bgcolor: COLORS.card
          }}
        >
          {editingRegistrant && (
            <RegistrantForm registrant={editingRegistrant} setRegistrant={setEditingRegistrant} eventId={eventId} />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, borderTop: `1px solid ${COLORS.border}`, flexShrink: 0 }}>
          <Button
            onClick={() => { setEditRegistrantDialogOpen(false); setEditingRegistrant(null); }}
            sx={{ color: COLORS.textSecondary, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleEditRegistrant}
            variant="contained"
            disabled={!editingRegistrant?.fullName?.trim() || !editingRegistrant?.dateOfBirth}
            sx={{
              bgcolor: COLORS.accent,
              '&:hover': { bgcolor: COLORS.accentHover },
              '&:disabled': { bgcolor: 'rgba(108, 93, 211, 0.3)' },
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DancerDetail;